import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AUTH_TYPE, FORM_SERVICE_TYPE, SERVICE_MODE, STATUS_TYPES, STRING_CONSTANTS } from '../../constants';
import { INameSpaceParam, IQueryPathParam, IServiceDetailsValidationModel } from '../../models';
import { useSelector } from '../../store/AppStore';
import useService from '../../store/_api/services/ServiceChecker';
import { serviceDetailsComplete, serviceDetailsIncomplete, serviceDetailsIncorrect } from '../../store/reducer/services/NavBarReducer';
import { setIsBackButtonWasPressedOnException, setShowServiceExistWarning } from '../../store/reducer/services/ServiceOperationMonitorReducer';
import { objToArrForQueryAndHeader } from '../../utilities/common/ObjectToArrayUtils';
import { addServiceDetailsToStore, getServiceDetailsDataFromReducer, getServiceDetailsFromForm, isServiceDetailsFormInvalid, validateXmlData } from '../../utilities/services/ServiceDetailsUtils';
import { getModeFromUrl } from '../../utilities/services/ServiceListUtils';

export const useServiceDetailsFormLaunch = (
  pHeaderParamList: [IQueryPathParam[], (param: IQueryPathParam[]) => void],
  pQueryPathParamList: [IQueryPathParam[], (param: IQueryPathParam[]) => void],
  pRequestParamList: [IQueryPathParam[], (param: IQueryPathParam[]) => void],
  pSoapNamespaceList: [INameSpaceParam[] | undefined, (param: INameSpaceParam[]) => void],
  pFormParamValues: [IServiceDetailsValidationModel, (param: IServiceDetailsValidationModel) => void],
  authTypeValue: string | undefined,
) => {
  const {
    checkClicked
  } = useSelector((state) => state.serviceDetailsReducer);
  const serviceMode = getModeFromUrl();
  const [formData, setFormParamValues] = pFormParamValues;
  const [headerParamList, setHeaderParamList] = pHeaderParamList;
  const [queryPathParamList, setQueryPathParamList] = pQueryPathParamList;
  const [requestParamList, setRequestParamList] = pRequestParamList;
  const [soapNamespaceList, setSoapNamespaceList] = pSoapNamespaceList;
  const { isBackButtonWasPressedOnException } = useSelector((state) => state.serviceOperationMonitorReducer);
  const serviceDetailsInReducer = useSelector((state) => state.serviceDetailsReducer);
  const dispatch = useDispatch();
  const [apiResponseStatus, setApiResponseStatus] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  const { searchServiceByName } = useService();
  const { t: translate } = useTranslation();

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  /**
   * @method serviceNameValidation
   * @description Validates the service name
   */
  const serviceNameValidation = () => {
    const serviceName = formData.serviceName.value;
    const newFormData = { ...formData };
    if (!formData.serviceName.isValid) {
      return;
    }
    if (serviceName) {
      setApiResponseStatus(true);
      // check for all status except inactive
      const statusList = [STATUS_TYPES.ACTIVE, STATUS_TYPES.DRAFT, STATUS_TYPES.RETURNED, STATUS_TYPES.PENDING_APPROVAL];
      const existingService = searchServiceByName(formData.serviceName.value, statusList, true);
      existingService.then((response) => {
        if (!isMountedRef.current) return;
        if (response.statusCode === STRING_CONSTANTS.STATUS_CODE_SUCCESS_DATA) {
          if (response?.serviceRecords?.totalRecords && response?.serviceRecords?.totalRecords > 0) {
            dispatch(setShowServiceExistWarning(true));
          } else {
            dispatch(setShowServiceExistWarning(false));
          }
        }
        setApiResponseStatus(false);
      }).catch(() => {
        if (!isMountedRef.current) return;
        setApiResponseStatus(false);
        dispatch(setShowServiceExistWarning(false));
      });
    } else {
      newFormData.serviceName.isValid = false;
      newFormData.serviceName.error = String(translate('service_validation_error.serviceNameRequired'));
      newFormData.serviceName.isTouched = true;
      setFormParamValues(newFormData);
    }
  };

  /**
   * @method checkEquality
   * @description Checks the equality of reducer service details and form service details
   * @returns true if equal else false
   */
  const checkEquality = () => {
    const reducerData = getServiceDetailsDataFromReducer(serviceDetailsInReducer);
    const dataInForm = getServiceDetailsFromForm(formData, headerParamList, queryPathParamList, requestParamList, soapNamespaceList);
    return (
      JSON.stringify(reducerData) === JSON.stringify(dataInForm)
    );
  };

  const shouldAddServiceDetailsToStore = () => {
    const isEqual = checkEquality();
    return (!isEqual && serviceMode === SERVICE_MODE.EDIT) || ((serviceMode !== SERVICE_MODE.EDIT && serviceMode !== SERVICE_MODE.VIEW) && !isEqual);
  };

  const isSoapServiceDataValid = (serviceType: string) => (serviceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.SOAPSERVICE && soapNamespaceList && soapNamespaceList?.length < 1);

  const isRestServiceDataValid = (serviceType: string) => (serviceType?.toLocaleLowerCase() === FORM_SERVICE_TYPE.REST
    && authTypeValue === `${AUTH_TYPE[2].value}`
    && headerParamList.length < 1
  );

  /**
   * @method validateFormDataForUI
   * @description Validates form data
   */
  const validateFormDataForUI = () => {
    const formServiceType = [FORM_SERVICE_TYPE.REST, FORM_SERVICE_TYPE.DB2, FORM_SERVICE_TYPE.MQ];
    if (formData) {
      const isFormInvalid = isServiceDetailsFormInvalid(formData, checkClicked);
      if ((isFormInvalid || isRestServiceDataValid(formData.serviceType?.value)
        || isSoapServiceDataValid(formData.serviceType?.value)) && (serviceMode !== SERVICE_MODE.VIEW)) {
        dispatch(serviceDetailsIncomplete());
        if (formServiceType.includes(formData.serviceType?.value?.toLocaleLowerCase()) && formData.endpoint.value === ''
        ) {
          dispatch(serviceDetailsIncorrect());
        }
        return;
      }
      dispatch(serviceDetailsComplete());
      if (shouldAddServiceDetailsToStore() && !isBackButtonWasPressedOnException) {
        addServiceDetailsToStore(formData, dispatch, checkClicked, headerParamList, requestParamList, queryPathParamList, soapNamespaceList);
      }
      if (isBackButtonWasPressedOnException) dispatch(setIsBackButtonWasPressedOnException(false));
    }
  };

  // Retrieving list data of RequestParamList
  const requestParamListHandler = (data: IQueryPathParam[]) => {
    setRequestParamList(objToArrForQueryAndHeader(data));
  };

  // Retrieving list data of QueryPathParamList
  const queryPathParamListHandler = (data: IQueryPathParam[]) => {
    setQueryPathParamList(objToArrForQueryAndHeader(data));
  };

  // Retrieving list data of Namespace
  const soapNamespaceListHandler = (data: INameSpaceParam[]) => {
    setSoapNamespaceList(data);
  };

  // Retrieving list data of HeaderParamList
  const headerParamListHandler = (data: IQueryPathParam[]) => {
    setHeaderParamList(objToArrForQueryAndHeader(data));
  };

  /**
   * @method validateXml
   * @description validates the xml
   */
  const validateXml = () => {
    setFormParamValues(validateXmlData(formData, translate));
  };

  /**
  * Sets the param list empty
  */
  const setParamsListEmpty = () => {
    setHeaderParamList([]);
    setQueryPathParamList([]);
    setRequestParamList([]);
  };

  return {
    apiResponseStatus,
    serviceNameValidation,
    validateFormDataForUI,
    headerParamListHandler,
    requestParamListHandler,
    queryPathParamListHandler,
    soapNamespaceListHandler,
    validateXml,
    setParamsListEmpty,
  };
};
